'use client';

import Icon from '@/components/ui/AppIcon';

interface ActivityBasedTrackingProps {
  currentLanguage: string;
}

const ActivityBasedTracking = ({ currentLanguage }: ActivityBasedTrackingProps) => {
  const trackingMethods = [
    {
      title: 'Direct Metering',
      icon: 'SignalIcon',
      description: 'Real-time data from IoT sensors and smart meters',
      accuracy: '99%',
      color: 'success',
      features: ['Continuous monitoring', 'Automated data capture', 'Real-time alerts', 'Historical trending'],
    },
    {
      title: 'Fuel Consumption',
      icon: 'FireIcon',
      description: 'Track fuel usage across fleet and facilities',
      accuracy: '95%',
      color: 'warning',
      features: ['Vehicle tracking', 'Fuel card integration', 'Usage patterns', 'Efficiency metrics'],
    },
    {
      title: 'Process Monitoring',
      icon: 'CogIcon',
      description: 'Industrial process emission calculations',
      accuracy: '92%',
      color: 'primary',
      features: ['Mass balance', 'Chemical reactions', 'Waste streams', 'By-products'],
    },
    {
      title: 'Material Flow',
      icon: 'ArrowsRightLeftIcon',
      description: 'Track materials throughout production cycle',
      accuracy: '90%',
      color: 'info',
      features: ['Inventory tracking', 'Supplier data', 'Waste analysis', 'Recycling rates'],
    },
  ];

  const comparison = [
    {
      aspect: 'Data Accuracy',
      spendBased: 'Low (±30%)',
      activityBased: 'High (±5%)',
      advantage: 'activity',
    },
    {
      aspect: 'Real-time Monitoring',
      spendBased: 'Not Available',
      activityBased: 'Live Updates',
      advantage: 'activity',
    },
    {
      aspect: 'Granular Insights',
      spendBased: 'Limited',
      activityBased: 'Detailed',
      advantage: 'activity',
    },
    {
      aspect: 'Implementation Effort',
      spendBased: 'Low',
      activityBased: 'Moderate',
      advantage: 'spend',
    },
    {
      aspect: 'Cost',
      spendBased: 'Low',
      activityBased: 'Higher Initial',
      advantage: 'spend',
    },
    {
      aspect: 'Actionable Data',
      spendBased: 'Generic',
      activityBased: 'Specific',
      advantage: 'activity',
    },
  ];

  return (
    <section className="py-20 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">
            Activity-Based Tracking Methods
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Transition from spend-based estimates to precise activity-level monitoring
          </p>
        </div>

        {/* Tracking Methods Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          {trackingMethods.map((method, index) => (
            <div
              key={index}
              className="bg-card border border-border rounded-xl p-6 hover:shadow-natural-lg transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`p-3 bg-${method.color}/10 rounded-lg`}>
                  <Icon name={method.icon} size={32} className={`text-${method.color}`} />
                </div>
                <div className="text-right">
                  <div className={`text-2xl font-heading font-bold text-${method.color}`}>
                    {method.accuracy}
                  </div>
                  <div className="text-xs text-text-secondary">Accuracy</div>
                </div>
              </div>

              <h3 className="font-heading font-bold text-xl text-text-primary mb-2">{method.title}</h3>
              <p className="text-sm text-text-secondary mb-4">{method.description}</p>

              <div className="space-y-2">
                {method.features.map((feature, idx) => (
                  <div key={idx} className="flex items-center space-x-2">
                    <Icon name="CheckCircleIcon" size={16} className="text-success" />
                    <span className="text-sm text-text-primary">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Comparison Table */}
        <div className="bg-card border border-border rounded-2xl p-8">
          <h3 className="font-heading font-bold text-2xl text-text-primary mb-8 text-center">
            Spend-Based vs Activity-Based Comparison
          </h3>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-border">
                  <th className="text-left py-4 px-4 font-heading font-bold text-text-primary">Aspect</th>
                  <th className="text-center py-4 px-4 font-heading font-bold text-warning">Spend-Based</th>
                  <th className="text-center py-4 px-4 font-heading font-bold text-success">Activity-Based</th>
                </tr>
              </thead>
              <tbody>
                {comparison.map((item, index) => (
                  <tr key={index} className="border-b border-border hover:bg-surface/50 transition-colors">
                    <td className="py-4 px-4 font-semibold text-text-primary">{item.aspect}</td>
                    <td className="py-4 px-4 text-center">
                      <span className={`${item.advantage === 'spend' ? 'text-success font-semibold' : 'text-text-secondary'}`}>
                        {item.spendBased}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <span className={`${item.advantage === 'activity' ? 'text-success font-semibold' : 'text-text-secondary'}`}>
                        {item.activityBased}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8 bg-primary/5 border border-primary/20 rounded-xl p-6">
            <div className="flex items-start space-x-3">
              <Icon name="LightBulbIcon" size={24} className="text-primary mt-1 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-primary mb-2">Transition Strategy</h4>
                <p className="text-sm text-text-primary">
                  Our platform enables phased migration from spend-based to activity-based tracking, starting with high-impact emission sources and gradually expanding coverage while maintaining continuous reporting.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ActivityBasedTracking;